import { useState } from 'react';
import Field from '../common/Field';
import CustomDropdown from '../forms/CustomDropdown';
import ui from '../../pages/dashboard/DashboardUI.module.css';
import { getCurrencySymbol } from '../../utils/currency';
import { INDUSTRIES, COUNTRIES, SIZE_BANDS, MODULES } from '../../utils/onboarding';
import styles from './Onboarding.module.css';

const COUNTRY_OPTIONS = COUNTRIES.map((c) => ({ value: c.value, label: `${c.value} (${c.currency}, ${getCurrencySymbol(c.currency)})` }));

export default function CompanyStep({ company, onSave, onSkip }) {
  const [form, setForm] = useState({
    industry: company.industry || INDUSTRIES[0],
    country: company.country || COUNTRIES[0].value,
    size_band: company.size_band || SIZE_BANDS[0].value,
    pinned_modules: company.pinned_modules || [],
    weekly_hours: company.weekly_hours || 40,
    annual_leave_days: company.annual_leave_days ?? 15,
  });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));
  const toggleModule = (key) => set('pinned_modules')(
    form.pinned_modules.includes(key) ? form.pinned_modules.filter((k) => k !== key) : [...form.pinned_modules, key]
  );

  const submit = async (e) => {
    e.preventDefault();
    const hours = Number(form.weekly_hours);
    const days = Number(form.annual_leave_days);
    if (!(hours >= 1 && hours <= 168)) return setError('Working hours per week must be between 1 and 168.');
    if (!Number.isInteger(days) || days < 0 || days > 365) return setError('Annual leave days must be a whole number from 0 to 365.');

    setBusy(true);
    const message = await onSave({
      ...form,
      weekly_hours: hours,
      annual_leave_days: days,
      currency: COUNTRIES.find((c) => c.value === form.country).currency,
    });
    if (message) setError(message);
    setBusy(false);
  };

  return (
    <form onSubmit={submit} noValidate>
      {error && <div className={styles.error} role="alert">{error}</div>}

      <div className={styles.row}>
        <Field label="Industry">
          <CustomDropdown fullWidth value={form.industry} onChange={set('industry')} options={INDUSTRIES.map((i) => ({ value: i, label: i }))} />
        </Field>
        <Field label="Country & currency">
          <CustomDropdown fullWidth value={form.country} onChange={set('country')} options={COUNTRY_OPTIONS} />
        </Field>
      </div>

      <Field label="Team size">
        <div className={ui.viewToggleGroup} role="radiogroup" aria-label="Team size" style={{ display: 'flex' }}>
          {SIZE_BANDS.map((b) => (
            <button
              key={b.value}
              type="button"
              role="radio"
              aria-checked={form.size_band === b.value}
              className={`${ui.viewToggleButton} ${form.size_band === b.value ? ui.viewToggleButtonActive : ''}`}
              style={{ flex: 1 }}
              onClick={() => set('size_band')(b.value)}
            >
              {b.label}
            </button>
          ))}
        </div>
      </Field>

      <Field label="What will you use Nodel for?">
        <div className={styles.chips}>
          {MODULES.map((m) => {
            const on = form.pinned_modules.includes(m.key);
            return (
              <button key={m.key} type="button" aria-pressed={on} className={`${styles.chip} ${on ? styles.chipSelected : ''}`} onClick={() => toggleModule(m.key)}>
                {on ? '✓' : '+'} {m.label}
              </button>
            );
          })}
        </div>
        <span className={styles.note} style={{ margin: 0 }}>We'll pin these to your sidebar first. Everything stays available.</span>
      </Field>

      <div className={styles.row}>
        <Field label="Working hours per week">
          <input type="number" min="1" max="168" value={form.weekly_hours} onChange={(e) => set('weekly_hours')(e.target.value)} />
        </Field>
        <Field label="Annual leave days">
          <input type="number" min="0" max="365" value={form.annual_leave_days} onChange={(e) => set('annual_leave_days')(e.target.value)} />
        </Field>
      </div>

      <div className={styles.actions} style={{ justifyContent: 'flex-end' }}>
        <div className={styles.actionsRight}>
          <button type="button" className={styles.link} onClick={onSkip}>Skip for now</button>
          <button type="submit" className={styles.primaryButton} disabled={busy}>{busy ? 'Saving…' : 'Continue'}</button>
        </div>
      </div>
    </form>
  );
}
